import { Container, Graphics, Sprite, Text } from 'pixi.js';
import type { Texture, TextStyleOptions } from 'pixi.js';
import { COLORS, FONT_FAMILY, lighten } from './theme';

const BUTTON_WIDTH = 220;
const BUTTON_HEIGHT = 64;
const BUTTON_RADIUS = 14;

// Rounded-rect button. Hover changes fill colour only (see theme.lighten) -
// no positional "lift" on hover, per the house style rule against it.
export function makeButton(label: string, onClick: () => void): Container {
  const container = new Container();
  const bg = new Graphics();

  const draw = (fill: number): void => {
    bg.clear();
    bg.roundRect(0, 0, BUTTON_WIDTH, BUTTON_HEIGHT, BUTTON_RADIUS).fill(fill);
  };
  draw(COLORS.accentRed);

  const text = makeLabel(label, {
    fontSize: 22,
    fontWeight: '900',
    fill: COLORS.textLight,
  });
  text.anchor.set(0.5);
  text.x = BUTTON_WIDTH / 2;
  text.y = BUTTON_HEIGHT / 2;

  container.addChild(bg, text);
  container.eventMode = 'static';
  container.cursor = 'pointer';
  container.on('pointerover', () => draw(lighten(COLORS.accentRed)));
  container.on('pointerout', () => draw(COLORS.accentRed));
  container.on('pointertap', onClick);

  return container;
}

// ui/button.png is 768x278: a wooden pill button with a ~60px rounded
// corner radius and a ~20px hard drop-shadow skirt along the bottom edge.
// Both of those non-stretchable features are large relative to the whole
// image (corner radius alone needs 2*60=120px just to not get clipped, and
// the shadow pushes the true bottom inset past 80px), so nine-slicing this
// art down to a normal button height forces leftWidth+rightWidth or
// topHeight+bottomHeight past the target size. Pixi's NineSliceSprite
// responds by silently shrinking the insets per axis, which squished this
// button's corners into a "boat" shape (bug: 78px tall button with 60px
// insets on all sides). A uniformly scaled plain Sprite can never distort
// the art - it always reproduces the source proportions exactly - so that's
// what we use here; the only cost is the button can't be stretched to an
// arbitrary aspect ratio, which none of our buttons need.
const IMAGE_BUTTON_WIDTH = 220;
const IMAGE_BUTTON_HEIGHT = 80;
const IMAGE_BUTTON_HOVER_ALPHA = 0.85;

// Button whose face is the generated ui/button.png art, uniformly scaled to
// fit inside the given width/height box (preserving the art's aspect ratio,
// so it always matches the source - see IMAGE_BUTTON_WIDTH comment above)
// instead of a flat Graphics fill. Hover dims the face's alpha slightly -
// still no positional "lift".
export function makeImageButton(
  texture: Texture,
  label: string,
  onClick: () => void,
  opts?: { width?: number; height?: number; textFill?: number; tint?: number },
): Container {
  const boxWidth = opts?.width ?? IMAGE_BUTTON_WIDTH;
  const boxHeight = opts?.height ?? IMAGE_BUTTON_HEIGHT;
  const scale = Math.min(boxWidth / texture.width, boxHeight / texture.height);
  const width = texture.width * scale;
  const height = texture.height * scale;

  const container = new Container();
  const face = new Sprite(texture);
  face.width = width;
  face.height = height;
  if (opts?.tint !== undefined) face.tint = opts.tint;

  const text = makeLabel(label, {
    fontSize: 22,
    fontWeight: '900',
    fill: opts?.textFill ?? COLORS.textDark,
  });
  text.anchor.set(0.5);
  text.x = width / 2;
  text.y = height / 2;

  container.addChild(face, text);
  container.eventMode = 'static';
  container.cursor = 'pointer';
  container.on('pointerover', () => {
    face.alpha = IMAGE_BUTTON_HOVER_ALPHA;
  });
  container.on('pointerout', () => {
    face.alpha = 1;
  });
  container.on('pointertap', onClick);

  return container;
}

export interface TextInputHandle {
  readonly container: Container;
  readonly value: string;
  focus(): void;
  /** Removes the backing hidden DOM <input>. Call from the owning scene's exit(). */
  destroy(): void;
}

export interface TextInputOptions {
  initialValue?: string;
  /** Applied to the raw DOM value on every input event (e.g. uppercase + strip non-letters). */
  filter?: (raw: string) => string;
}

const INPUT_WIDTH = 320;
const INPUT_HEIGHT = 48;
const INPUT_RADIUS = 10;

// A rounded-rect input rendered in Pixi, backed by a visually-hidden DOM
// <input> so mobile browsers show a real virtual keyboard. Tapping the Pixi
// container focuses the DOM input; its value drives the displayed text.
export function makeTextInput(placeholder: string, maxLen: number, opts: TextInputOptions = {}): TextInputHandle {
  const container = new Container();
  const bg = new Graphics()
    .roundRect(0, 0, INPUT_WIDTH, INPUT_HEIGHT, INPUT_RADIUS)
    .fill(COLORS.panel);
  bg.stroke({ width: 2, color: COLORS.outline });

  const label = makeLabel(placeholder, { fontSize: 20, fill: COLORS.outline });
  label.x = 16;
  label.y = (INPUT_HEIGHT - label.height) / 2;

  container.addChild(bg, label);
  container.eventMode = 'static';
  container.cursor = 'text';

  const dom = document.createElement('input');
  dom.type = 'text';
  dom.maxLength = maxLen;
  dom.autocomplete = 'off';
  dom.setAttribute('aria-hidden', 'true');
  dom.style.position = 'fixed';
  dom.style.left = '-9999px';
  dom.style.top = '0';
  dom.style.width = '1px';
  dom.style.height = '1px';
  dom.style.opacity = '0';
  document.body.appendChild(dom);
  if (opts.initialValue) dom.value = opts.initialValue;

  const sync = (): void => {
    if (opts.filter) {
      const filtered = opts.filter(dom.value);
      if (filtered !== dom.value) dom.value = filtered;
    }
    label.text = dom.value.length > 0 ? dom.value : placeholder;
    label.style.fill = dom.value.length > 0 ? COLORS.textDark : COLORS.outline;
  };
  dom.addEventListener('input', sync);
  container.on('pointertap', () => dom.focus());
  sync();

  return {
    container,
    get value(): string {
      return dom.value;
    },
    focus(): void {
      dom.focus();
    },
    destroy(): void {
      dom.removeEventListener('input', sync);
      dom.remove();
    },
  };
}

export function makeLabel(text: string, style?: TextStyleOptions): Text {
  return new Text({
    text,
    style: {
      fontFamily: FONT_FAMILY,
      fontSize: 18,
      fill: COLORS.textDark,
      ...style,
    },
  });
}
